import { Skeleton } from "@mui/material";
import dayjs from "dayjs";
import {
  ArrowRight,
  Tags,
  Briefcase,
  Calendar,
  GraduationCap,
  Clock,
  MapPin,
  Video,
} from "lucide-react";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { ClickableContainer, PseudoLink } from "../components/StyledComponents";
import EventsDetails from "../DetailFunctions/EventsDetails";
import useFetchConfig from "../fetchFunction/ConfigFetch";
import useFetchPaginatedElements from "../fetchFunction/OverviewFetch";
import Pagination from "../Pagination";
import "../styles/EventsOverview.css";

const formatEventDate = (dateTime) => {
  return dateTime ? dayjs(dateTime).format("DD.MM.YYYY") : null;
};

const formatEventDuration = (startDate, startTime, endDate, endTime) => {
  if (startDate === endDate) {
    return (
      <>
        <p className="event-date">
          <Calendar className="event-icon" size={16} /> {startDate}
        </p>
        {startTime && endTime && (
          <p className="event-time">
            <Clock className="event-icon" size={16} />
            {startTime} - {endTime} Uhr
          </p>
        )}
      </>
    );
  } else {
    return (
      <p className="event-date">
        <Calendar className="event-icon" size={16} />
        {`${startDate} ${startTime || ""} bis ${endDate} ${endTime || ""} Uhr`}
      </p>
    );
  }
};

const validateEvents = (data) => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  return data.map((item) => ({
    id: item.id || null,
    headline: item.headline || "Kein Titel verfügbar",
    imageUrl: item.image_urls || null,
    category:
      Array.isArray(item.category) && item.category.length > 0
        ? item.category.map((cat) => cat.name).join(", ")
        : null,
    location: item.location || "Online",
    eventDateStart: formatEventDate(item.eventDurationStart?.split(" ")[0]),
    eventTimeStart: item.eventDurationStart?.split(" ")[1] || null,
    eventDateEnd: formatEventDate(item.eventDurationEnd?.split(" ")[0]),
    eventTimeEnd: item.eventDurationEnd?.split(" ")[1] || null,
    company: item.company || "Keine Firma angegeben",
    text: item.text || "Keine Beschreibung verfügbar",
  }));
};

const getEventIcon = (category) => {
  if (!category) return <Tags size={30} />;
  if (category.includes("Webinar")) return <Video size={16} />;
  if (category.includes("Seminar") || category.includes("Schulung"))
    return <GraduationCap size={16} />;
  if (category.includes("Messe") || category.includes("Pressetermin"))
    return <Briefcase size={16} />;

  return <Tags size={30} />;
};

const EventOverview = ({ styling, companyAccessname }) => {
  const { configSettings } = useFetchConfig();
  const defaultPageSize = configSettings?.pageSize || 12;
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultPageSize);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [showDetailPage, setShowDetailPage] = useState(false);

  const {
    loading,
    error,
    totalPages,
    pageSize,
    events: eventData,
  } = useFetchPaginatedElements(
    companyAccessname,
    "events",
    currentPage,
    itemsPerPage
  );

  useEffect(() => {
    if (pageSize && pageSize !== itemsPerPage) {
      setItemsPerPage(pageSize);
    }
  }, [pageSize]);

  useEffect(() => {
    if (!showDetailPage) {
      window.scrollTo(0, 0);
    }
  }, [showDetailPage]);

  const validEvents = validateEvents(eventData);

  const truncateText = (text, maxLength) => {
    if (!text || text.length <= maxLength) return text;

    return text.substring(0, maxLength) + "...";
  };

  if (loading) {
    return (
      <div>
        {Array.from(new Array(itemsPerPage)).map((_, index) => (
          <ClickableContainer key={index} styling={styling}>
            <Skeleton variant="text" width="80%" height={30} />
            <Skeleton variant="rectangular" width="100px" height="100px" />
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="rectangular" width="60%" height={30} />
          </ClickableContainer>
        ))}
      </div>
    );
  }

  if (error) {
    return <p>Fehler: {error.message || "Ein Fehler ist aufgetreten."}</p>;
  }

  if (validEvents.length === 0) {
    return <p>Keine Events verfügbar.</p>;
  }

  return (
    <div>
      {!showDetailPage ? (
        <>
          {validEvents.map((event, index) => (
            <ClickableContainer
              key={index}
              styling={styling}
              onClick={() => {
                setSelectedEventId(event.id);
                setShowDetailPage(true);
              }}
            >
              <h3
                className="chapter-title"
                style={{ color: styling.textColor }}
              >
                {event.headline}
              </h3>
              <div className="event-content">
                {event.imageUrl && (
                  <img
                    src={event.imageUrl}
                    alt={event.headline || "Event image"}
                    className="event-image"
                  />
                )}
                <div className="event-textblock">
                  <p style={{ color: styling.textColor }}>
                    {truncateText(event.text, 150)}
                  </p>
                </div>
              </div>
              <div className="event-details">
                {event.category && (
                  <p className="event-category">
                    {getEventIcon(event.category)} {event.category}
                  </p>
                )}
                {event.location && (
                  <p className="event-location">
                    <MapPin
                      className="event-icon"
                      size={16}
                      style={{ color: styling.iconColor }}
                    />
                    {event.location}
                  </p>
                )}
                {formatEventDuration(
                  event.eventDateStart,
                  event.eventTimeStart,
                  event.eventDateEnd,
                  event.eventTimeEnd
                )}
              </div>
              <PseudoLink className="pseudo-link" styling={styling}>
                weiterlesen{" "}
                <ArrowRight
                  className="arrow-icon"
                  size={16}
                  style={{ color: styling.iconColor }}
                />
              </PseudoLink>
            </ClickableContainer>
          ))}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            styling={styling}
            onChangePage={setCurrentPage}
          />
        </>
      ) : (
        <EventsDetails
          eventId={selectedEventId}
          styling={styling}
          companyAccessname={companyAccessname}
          onClose={() => setShowDetailPage(false)}
        />
      )}
    </div>
  );
};

EventOverview.propTypes = {
  styling: PropTypes.object.isRequired,
  companyAccessname: PropTypes.string.isRequired,
};

export default EventOverview;
